import React, { useMemo } from 'react'
import { useBillingContext } from '~/contexts/BillingContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { formatDisplayDate } from '~/utils/timezone'
import { cm } from '~/utils/utils'
import { Selector } from '../common/Selector'
import { BillingPeriodDropdown } from './BillingPeriodDropdown'
import { getIsBillableFlowPlan, type CreditsData, type UsageOverviewData } from './types'
import { UsageStatsCards } from './UsageStatsCards'

export type TimeRangeMode = 'billing_period' | 'date_range'

interface UsageDisplayContentProps {
  // Usage data
  usageOverview?: UsageOverviewData
  credits?: CreditsData

  // Filter state
  selectedWorkspaceUuid?: string
  onWorkspaceChange?: (uuid: string | undefined) => void

  // Time range mode
  timeRangeMode?: TimeRangeMode
  onTimeRangeModeChange?: (mode: TimeRangeMode) => void
  customStartDate?: string
  customEndDate?: string
  onCustomStartDateChange?: (date: string) => void
  onCustomEndDateChange?: (date: string) => void
  /** Inclusive YYYY-MM-DD lower bound for the custom range picker (default: unrestricted) */
  minSelectableDate?: string
  /** Inclusive YYYY-MM-DD upper bound for the custom range picker (default: unrestricted) */
  maxSelectableDate?: string
  /**
   * When viewing another tenant (e.g. super admin), use these labels for the billing
   * period dropdown instead of the signed-in user's BillingContext licenses.
   */
  billingPeriodForDisplay?: { periodStart?: string | null; periodEnd?: string | null }

  // Display options
  showWorkspaceFilter?: boolean
  showTimeRangeSelector?: boolean
  layout?: 'horizontal' | 'compact'
  className?: string

  // Custom credits display (replaces default CreditsCard)
  customCreditsSection?: React.ReactNode

  // Additional content (e.g., download button, charts, etc.)
  headerActions?: React.ReactNode
  children?: React.ReactNode

  /** Called when the user clicks the credits card to view flow details */
  onCreditsDetailsClick?: () => void
  /** Super admins can see credits; non-super-admins have credits hidden */
  isSuperAdmin?: boolean
  /** Custom title for the credits section (e.g. "Team Credit Usage") */
  creditsSectionTitle?: string
  /** Custom title for the billable flows card (e.g. "Team Billable Flows") */
  billableFlowsTitle?: string
}

export const UsageDisplayContent: React.FC<UsageDisplayContentProps> = ({
  usageOverview,
  credits,
  selectedWorkspaceUuid,
  onWorkspaceChange,
  timeRangeMode = 'billing_period',
  onTimeRangeModeChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  minSelectableDate,
  maxSelectableDate,
  billingPeriodForDisplay,
  showWorkspaceFilter = true,
  showTimeRangeSelector = true,
  layout = 'horizontal',
  className,
  customCreditsSection,
  headerActions,
  children,
  onCreditsDetailsClick,
  isSuperAdmin = false,
  creditsSectionTitle,
  billableFlowsTitle
}) => {
  const isCompactViewport = useCompactViewport()
  const { workspaces } = useWorkspaceContext()
  const { allLicenses } = useBillingContext()

  // Workspace options
  const workspaceOptions = useMemo(() => {
    const options = [{ key: 'all', value: '', label: 'All Workspaces' }]
    workspaces?.forEach(ws => {
      options.push({
        key: String(ws.id),
        value: ws.uuid,
        label: ws.name
      })
    })
    return options
  }, [workspaces])

  /** Format dates for display using centralized timezone-safe utility */
  const formatDate = (dateString?: string) => formatDisplayDate(dateString ?? null)

  // Billing period from allLicenses (combined/primary period from backend)
  const periodStart = billingPeriodForDisplay?.periodStart ?? allLicenses?.period_start
  const periodEnd = billingPeriodForDisplay?.periodEnd ?? allLicenses?.period_end
  const isBillableFlowPlan = getIsBillableFlowPlan(credits, allLicenses)

  // Show limit/denominator only for team dashboard in billing period mode.
  // Personal usage (customCreditsSection) always passes showLimits=false.
  const showLimits = timeRangeMode === 'billing_period'

  // Billable flows are account-level metrics, only show when viewing all workspaces
  const isAllWorkspacesSelected = !selectedWorkspaceUuid || selectedWorkspaceUuid === ''

  // Compute modified credits to hide limits for date range mode
  const displayCredits = useMemo(() => {
    if (!credits) return undefined
    if (showLimits) return credits
    // For date range mode, set total to undefined to hide denominator
    return {
      ...credits,
      total: undefined
    }
  }, [credits, showLimits])

  return (
    <div className={cm('flex flex-col', className)}>
      {/* Filters Row */}
      <div
        className={cm(
          'flex justify-between',
          isCompactViewport ? 'mb-4 flex-col items-stretch gap-3' : 'mb-6 items-center'
        )}
      >
        <div
          className={cm(
            'flex gap-[12px] items-center',
            isCompactViewport ? 'w-full flex-col items-stretch' : 'flex-wrap'
          )}
        >
          {showWorkspaceFilter && onWorkspaceChange && (
            <Selector
              size='small'
              value={selectedWorkspaceUuid ?? ''}
              onSelectionChange={value => onWorkspaceChange(value || undefined)}
              options={workspaceOptions}
              className={isCompactViewport ? '!w-full' : '!w-auto'}
              triggerClassName={cm(
                '!h-[48px] !px-[16px] !py-3 !rounded-[12px] !border !border-tokens-neutral-border-1 !bg-transparent',
                isCompactViewport ? '!w-full' : '!w-auto'
              )}
              valueClassName='!max-w-none'
              dropdownClassName='!max-h-[280px] !overflow-y-auto'
            />
          )}

          {showTimeRangeSelector && onTimeRangeModeChange && (
            <BillingPeriodDropdown
              timeRangeMode={timeRangeMode}
              onTimeRangeModeChange={onTimeRangeModeChange}
              periodStart={periodStart}
              periodEnd={periodEnd}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              formatDate={formatDate}
              onCustomStartDateChange={onCustomStartDateChange}
              onCustomEndDateChange={onCustomEndDateChange}
              minSelectableDate={minSelectableDate}
              maxSelectableDate={maxSelectableDate}
              className={isCompactViewport ? 'w-full' : undefined}
            />
          )}
        </div>
        {headerActions && (
          <div className={cm(isCompactViewport ? 'self-start' : 'self-auto')}>{headerActions}</div>
        )}
      </div>

      {/* Stats Cards */}
      {usageOverview && displayCredits && !customCreditsSection && (
        <UsageStatsCards
          usageOverview={usageOverview}
          credits={displayCredits}
          layout={layout}
          className='mb-6'
          showLimits={showLimits}
          isAllWorkspacesSelected={isAllWorkspacesSelected}
          isBillableFlowPlan={isBillableFlowPlan}
          onCreditsDetailsClick={onCreditsDetailsClick}
          isSuperAdmin={isSuperAdmin}
          creditsSectionTitle={creditsSectionTitle}
          billableFlowsTitle={billableFlowsTitle}
        />
      )}

      {/* Custom Credits Section (for personal usage view) */}
      {usageOverview && customCreditsSection && <div className='mb-6'>{customCreditsSection}</div>}

      {/* Additional content (charts, activities, etc.) */}
      {children}
    </div>
  )
}

export default UsageDisplayContent
